import { useState, useRef, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useUserRoles, ROLE_LABELS } from '@/contexts/UserRolesContext'
import styles from '@/styles/user-profile.module.css'

function getInitials(name?: string | null, email?: string | null): string {
  if (name) {
    const parts = name.trim().split(/\s+/)
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
    return parts[0][0].toUpperCase()
  }
  if (email) return email[0].toUpperCase()
  return 'U'
}

export function UserProfile() {
  const { user, logout } = useAuth()
  const { role } = useUserRoles()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])

  const initials = getInitials(user?.name, user?.email)
  const displayName = user?.name || user?.email || 'User'
  const roleLabel = ROLE_LABELS[role] ?? 'Standard User'

  return (
    <div className={styles.userProfile} ref={ref}>
      <button
        className={styles.trigger}
        onClick={() => setOpen(o => !o)}
        aria-label="User profile"
        aria-expanded={open}
      >
        <div className={styles.avatar}>{initials}</div>
      </button>

      {open && (
        <div className={styles.dropdown} role="menu">
          <div className={styles.header}>
            <div className={styles.headerAvatar}>{initials}</div>
            <div className={styles.headerInfo}>
              <div className={styles.headerName}>{displayName}</div>
              {user?.email && <div className={styles.headerEmail}>{user.email}</div>}
            </div>
          </div>

          <div className={styles.body}>
            <div className={styles.roleRow}>
              <span className={styles.roleLabel}>Role</span>
              <span className={`${styles.roleBadge} ${styles[`role_${role}`] ?? ''}`}>
                {roleLabel}
              </span>
            </div>
          </div>

          <div className={styles.footer}>
            <button
              className={styles.signOut}
              onClick={() => { setOpen(false); logout() }}
            >
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
